import { Cell } from './Cell';
import {
    DataListCell,
    DataListCellValue,
    DataListColumn,
    NormalizedRowData,
    RowData,
    RowWithNormalizedData,
} from './types';
import { List } from './List';

export class Row {
    id: string;
    ordinal: string | number;
    cells: Array<Cell>;
    constructor(row: RowData, columnConfigs: Array<DataListColumn>) {
        this.id = row[0].row_id;
        this.ordinal = row[0].row_ordinal;

        this.cells = row.map((cell: DataListCell, key: number) => {
            return new Cell(cell, columnConfigs[key], this);
        });
    }

    /**
     * Get an object with properties of column names and their values for this row
     */
    serialize(): Record<string, unknown> {
        const serialRow: Record<string, unknown> = {
            row_id: this.id,
        };

        this.cells.forEach((cell: Cell) => {
            let value = cell.value;
            if (cell.isList()) {
                if (cell.value && cell.value instanceof List) {
                    value = cell.value.serialize();
                } else {
                    value = null;
                }
            }

            serialRow[this.toCamelCase(cell.name.toLowerCase())] = value;
        });

        return serialRow;
    }

    /**
     * Serialize list for signs list widget
     */
    serializeForSigns(
        withNormalized: boolean
    ): RowData | RowWithNormalizedData | Record<string, unknown> {
        const result: RowData | RowWithNormalizedData | Record<string, unknown> = {};

        this.cells.forEach(function (cell) {
            result[parseInt(cell.data.column_ordinal)] = cell.serializeForSigns(withNormalized);
        });
        if (withNormalized) {
            result.normalized = this.normalize();
        }
        return result;
    }

    /**
     * Normalize row by returning an object with cell names as key for their values
     */
    normalize(): NormalizedRowData | Record<string, unknown> {
        return this.cells.reduce(
            (previousValue: NormalizedRowData | Record<string, unknown>, cell) => {
                if (!cell.isList() && cell.value) {
                    previousValue[cell.name] = cell.value;
                }
                return previousValue;
            },
            {}
        );
    }

    /**
     * Get an array of the non-empty cells of type LIST in this row
     */
    getListCells(): Array<Cell> {
        return this.cells.filter(function (cell) {
            return cell.isListWithData();
        });
    }

    /**
     * Get cell with a given column name
     */
    getCell(name: string): Cell | undefined {
        return this.cells.find((cell) => {
            return cell.name === name.toLowerCase();
        });
    }

    /**
     * Get value of a cell with a given column name
     */
    getCellValue(name: string): DataListCellValue | undefined {
        const cell = this.getCell(name);
        return cell ? cell.value : undefined;
    }

    /**
     * Check if cell with a give column name has a value
     */
    hasValueInCellNamed(name: string): boolean {
        const cell = this.getCell(name);
        return cell ? !!cell.value : false;
    }

    protected toCamelCase(words: string): string {
        return words
            .trim()
            .toLowerCase()
            .split(/[ -]/)
            .map((word: string, index: number) => {
                return index > 0 ? this.capitalize(word) : word;
            })
            .join('');
    }

    protected capitalize(word: string): string {
        const firstLetter = word.charAt(0).toUpperCase();
        return firstLetter + word.slice(1);
    }
}
